import {
  useMemo,
  useState,
  useEffect,
} from 'react';
import type {
  ChangeEvent,
} from 'react';
import {
  useWishlists,
  useAuthentication,
} from '@dinosave/core/stores';
import type {
  ItemEntity,
  ImageEntity,
  WishlistEntity,
} from '@dinosave/core/types';
import {
  Modal,
} from '@/components/Modals';
import type {
  ModalProps,
} from '@/components/Modals';
import {
  AUTH,
} from '@/constants/namespaces';
import Button from '@/ui/components/Button';
import Input from '@/ui/components/Input';
import Loader from '@/ui/components/Loader';
import Text from '@/ui/components/Text';
import {
  PlusIcon,
  DotsIcon,
  CloseIcon,
  SearchIcon,
  ShoppingBagIcon,
} from '@/ui/icons';

import styles from './SelectWishlistModal.module.css';

const DEFAULT_WISHLIST: WishlistEntity = {
  id: 0,
  name: 'Everything',
};

type SelectWishlistModalProps = ModalProps & {
  item?: ItemEntity;
  loading?: boolean;
  onChange?: (wishlist: WishlistEntity) => void | Promise<void>;
}

const SelectWishlistModal = (props: SelectWishlistModalProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    item,
    isOpen,
    onClose,
    onChange,
    ...rest
  } = props;

  const {
    image,
    userId,
    wishlist,
  } = item || {};

  const itemImage = image as ImageEntity;
  const imageUrl = itemImage?.small?.url;
  const wishlistName = wishlist?.name || 'Everything';

  // ##########################################################################
  // STORES
  // ##########################################################################

  const authenticatedUser = useAuthentication(s => s.authenticatedUser);
  const authenticatedUserId = authenticatedUser?.id;
  const authenticatedUserSlug = authenticatedUser?.slug;
  const authorized = authenticatedUserId &&
                     authenticatedUserId === userId;

  const wishlists = useWishlists(AUTH, s => s.wishlists) || [];
  const wishlistsLoading = useWishlists(AUTH, s => s.wishlistsLoading);
  const createUserWishlist = useWishlists(AUTH, s => s.createUserWishlist);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [search, setSearch] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const header = authorized
    ? `Move from "${wishlistName}" to`
    : 'Select a wishlist';
  const wishlistsCount = wishlists?.length || 0;
  const isEmpty = !wishlistsCount &&
                  !wishlistsLoading &&
                  !search.length;

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (
      createLoading
    ) return;

    setSearch(e.target.value);
  };

  const handleChange = (wishlist: WishlistEntity) => () => {
    onChange(wishlist);
  };

  const handleCreate = async () => {
    if (
      !authenticatedUser
    ) return;

    setCreateLoading(true);

    try {
      const trimmedName = search
        .trim()
        .replaceAll(/\s\s+/g, ' ');

      const wishlist = {
        name: trimmedName,
      };

      await createUserWishlist(authenticatedUserSlug, wishlist);
    } finally {
      setCreateLoading(false);
    }
  };

  // ##########################################################################
  // MEMOIZATION
  // ##########################################################################

  const existingWishlist = useMemo(() => (
    wishlists?.find((wishlist) => (
      wishlist.name.toLowerCase().trim() === search.toLowerCase().trim()
    ))
  ), [wishlists, search]);

  const filteredWishlists = useMemo(() => (
    wishlists?.filter((wishlist) => (
      wishlist.name.toLowerCase().includes(search.toLowerCase().trim())
    )) || []
  ), [wishlists, search]);

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    if (
      !isOpen
    ) return;

    setSearch('');
  }, [isOpen]);

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Modal
      {...rest}
      isOpen={isOpen}
      className={styles.selectWishlistModal}
      contentClassName={styles.content}
      onClose={onClose}
    >
      {item && (
        <div className={styles.header}>
          <div className={styles.headerImage}>
            <img
              alt={item.name}
              src={imageUrl}
            />
          </div>

          <div className={styles.headerText}>
            <Text
              kind="paragraph"
              size="large"
              weight="500"
            >
              {header}
            </Text>
          </div>

          <div className={styles.close}>
            <Button
              kind="ghost"
              color="secondary"
              onClick={onClose}
              centered
            >
              <CloseIcon />
            </Button>
          </div>

        </div>
      )}

      <div className={styles.search}>
        <Input
          value={search}
          onChange={handleSearchChange}
          placeholder="Add a wishlist..."
          icon={SearchIcon}
          autoFocus={isEmpty}
          autoComplete="off"
        />
      </div>

      <div className={styles.wishlists}>
        {!!search.length && !existingWishlist && (
          <div className={styles.create}>
            <Button
              color="secondary"
              kind="ghost"
              onClick={handleCreate}
            >
              <div className={styles.icon}>
                {createLoading ? (
                  <Loader color="neutral-500" />
                ) : (
                  <PlusIcon />
                )}
              </div>

              <Text
                kind="caption"
                size="large"
              >
                Add
              </Text>
              <Text
                kind="caption"
                size="large"
                weight="600"
              >
                &nbsp;&quot;{search.trim()}&quot;&nbsp;
              </Text>
            </Button>
          </div>
        )}

        {filteredWishlists.map((wishlist: WishlistEntity) => (
          <div
            key={wishlist.id}
            className={styles.wishlist}
          >
            <Button
              color="secondary"
              kind="ghost"
              onClick={handleChange(wishlist)}
            >
              <div className={styles.icon}>
                {wishlist?.emoji ? (
                  <Text
                    kind="caption"
                    size="large"
                  >
                    {wishlist.emoji}
                  </Text>
                ) : (
                  <ShoppingBagIcon />
                )}
              </div>

              <Text
                kind="caption"
                size="large"
                weight="500"
              >
                {wishlist.name}
              </Text>

              <div className={styles.itemsCount}>
                <Text
                  kind="caption"
                  size="large"
                  color="neutral-500"
                  weight="500"
                >
                  {wishlist.itemsCount}
                </Text>
              </div>
            </Button>
          </div>
        ))}

        {isEmpty && (
          <div className={styles.empty}>
            <Text
              kind="caption"
              size="large"
              color="neutral-500"
            >
              Start typing to create a wishlist
            </Text>
          </div>
        )}
      </div>

      <div className={styles.everything}>
        <Button
          color="secondary"
          kind="ghost"
          onClick={handleChange(DEFAULT_WISHLIST)}
        >
          <div className={styles.icon}>
            <DotsIcon />
          </div>

          <Text
            kind="caption"
            size="large"
            weight="500"
          >
            Everything
          </Text>
        </Button>
      </div>
    </Modal>
  );
};

export default SelectWishlistModal;
